import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { FaPlus, FaTrash, FaEdit, FaToggleOn, FaToggleOff, FaEye, FaVideo, FaSort, FaGripLines } from 'react-icons/fa';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './StoryManagement.css';

const StoryManagement = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Состояние для различных форм и режимов
  const [showStoryForm, setShowStoryForm] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [showSortMode, setShowSortMode] = useState(false);
  const [formMode, setFormMode] = useState('create');
  
  // Состояние для редактирования
  const [editingStory, setEditingStory] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedStoryId, setSelectedStoryId] = useState(null);
  
  // Состояние для формы истории
  const [title, setTitle] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [topColor, setTopColor] = useState('#f09433');
  const [bottomColor, setBottomColor] = useState('#e6683c');
  const [emoji, setEmoji] = useState('😊');
  
  // Состояние для формы элемента истории
  const [type, setType] = useState('image');
  
  // Состояние для загрузки файлов
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState('');
  
  const { user } = useAuth();
  
  // Настройка сенсоров для drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 250, // задержка перед началом перетаскивания в мс
        tolerance: 5, // небольшая погрешность для предотвращения случайных драгов
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Обработчик drag-and-drop сортировки
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      // Находим индексы элементов
      const oldIndex = stories.findIndex(story => story._id === active.id);
      const newIndex = stories.findIndex(story => story._id === over.id);
      
      // Создаем новый массив с измененным порядком
      const updatedStories = [...stories];
      const [removedStory] = updatedStories.splice(oldIndex, 1);
      updatedStories.splice(newIndex, 0, removedStory);
      
      // Обновляем состояние
      setStories(updatedStories);
      
      // Обновляем сортировку на сервере одним запросом
      try {
        const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
        
        // Получаем массив ID в правильном порядке
        const storyIds = updatedStories.map(story => story._id);
        console.log('Отправляем новый порядок сортировки:', storyIds);
        
        // Отправляем запрос на обновление сортировки
        const response = await fetch('/api/stories/update-sorting', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Telegram-ID': telegramId
          },
          body: JSON.stringify({ storyIds })
        });
        
        if (!response.ok) {
          throw new Error('Не удалось обновить сортировку');
        }
        
        const result = await response.json();
        console.log('Сортировка успешно обновлена:', result);
        
        // Обновляем список историй
        fetchStories();
        
      } catch (error) {
        console.error('Ошибка при обновлении порядка историй:', error);
        // Восстанавливаем исходный порядок при ошибке
        fetchStories();
      }
    }
  };

  // Переключение режима сортировки
  const toggleSortMode = () => {
    // Если форма создания/редактирования открыта, закрываем её
    if (showStoryForm) {
      setShowStoryForm(false);
      resetStoryForm();
    }
    
    if (showItemForm) {
      setShowItemForm(false);
      resetItemForm();
    }
    
    setShowSortMode(!showSortMode);
  };

  // Загрузка историй с сервера
  const fetchStories = useCallback(async () => {
    setLoading(true);
    try {
      // Получаем актуальные данные пользователя из контекста и localStorage
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      const isAdmin = user?.isAdmin || JSON.parse(localStorage.getItem('user'))?.isAdmin;
      
      if (!telegramId) {
        throw new Error('Пользователь не авторизован');
      }
      
      // Добавляем отладочный вывод
      console.log('[StoryManagement] Данные пользователя:', { 
        telegramId, 
        isAdmin: !!isAdmin, 
        user 
      });
      
      const headers = { 
        'X-Telegram-ID': telegramId, 
        'Content-Type': 'application/json' 
      };
      
      // Сначала пробуем получить истории как администратор
      console.log('[StoryManagement] Начало загрузки историй для админа');
      
      let response = await fetch('/api/stories/admin?sort=sortOrder', {
        headers: headers
      });
      
      console.log('[StoryManagement] Статус ответа от сервера (админ):', response.status);
      
      // Если получили ошибку 403, значит пользователь не администратор или произошла ошибка аутентификации
      if (response.status === 403) {
        const errorText = await response.text();
        console.warn('[StoryManagement] Ответ сервера:', errorText);
        console.log('[StoryManagement] Попытка загрузки обычных историй');
        
        // Пробуем получить обычные истории
        response = await fetch('/api/stories?sort=sortOrder', {
          headers: headers
        });
        
        console.log('[StoryManagement] Статус ответа для обычных историй:', response.status);
        
        if (!response.ok) {
          const publicErrorText = await response.text();
          console.error('[StoryManagement] Ошибка при загрузке обычных историй:', publicErrorText);
          throw new Error('Ошибка при загрузке историй');
        }
      } else if (!response.ok) {
        const errorText = await response.text();
        console.error('[StoryManagement] Ошибка от сервера:', errorText);
        throw new Error('Ошибка при загрузке историй');
      }
      
      const data = await response.json();
      
      if (!data || data.length === 0) {
        setStories([]);
        setLoading(false);
        return;
      }
      
      setStories(data);
      setLoading(false);
    } catch (err) {
      console.error('Ошибка при загрузке историй:', err);
      
      // Для режима разработки можно использовать тестовые данные
      if (process.env.NODE_ENV === 'development') {
        const mockStories = [
          {
            _id: '1',
            title: 'Тестовая история 1',
            isActive: true,
            items: [
              {
                _id: '1-1',
                type: 'image',
                imageUrl: 'https://picsum.photos/id/1/400/400',
                createdAt: new Date().toISOString()
              },
              {
                _id: '1-2',
                type: 'image',
                imageUrl: 'https://picsum.photos/id/124/400/400',
                createdAt: new Date().toISOString()
              }
            ],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          },
          {
            _id: '2',
            title: 'Тестовая история 2',
            isActive: false,
            items: [
              {
                _id: '2-1',
                type: 'video',
                videoUrl: 'https://example.com/video.mp4',
                createdAt: new Date().toISOString()
              }
            ],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }
        ];
        
        setStories(mockStories);
        setError("Используются тестовые данные: " + err.message);
      } else {
        setError(err.message);
      }
      
      setLoading(false);
    }
  }, [user]);
  
  useEffect(() => {
    fetchStories();
  }, [fetchStories]);
  
  // Сброс форм
  const resetStoryForm = () => {
    setTitle('');
    setIsActive(true);
    setTopColor('#f09433');
    setBottomColor('#e6683c');
    setEmoji('😊');
  };
  
  const resetItemForm = () => {
    setType('image');
    setFile(null);
    setFilePreview('');
  };
  
  // Обработчики для форм историй
  const handleAddStory = () => {
    resetStoryForm();
    setEditingStory(null); // Убедимся, что сбрасываем редактируемую историю
    setFormMode('create');
    setShowStoryForm(true);
    setShowItemForm(false);
  };
  
  const handleEditStory = (story) => {
    setEditingStory(story);
    setFormMode('edit');
    
    // Парсим существующий градиент, чтобы получить цвета
    let topColorValue = '#f09433'; 
    let bottomColorValue = '#e6683c'; 
    
    if (story.backgroundColor) {
      const colorMatches = story.backgroundColor.match(/#[0-9a-f]{6}|#[0-9a-f]{3}|rgba?\([^)]+\)/gi);
      if (colorMatches && colorMatches.length >= 2) {
        topColorValue = colorMatches[0];
        bottomColorValue = colorMatches[1];
      }
    }
    
    // Заполняем форму данными истории
    setTitle(story.title || '');
    setIsActive(story.isActive !== undefined ? story.isActive : true);
    setTopColor(topColorValue);
    setBottomColor(bottomColorValue);
    setEmoji(story.emoji || '😊');

    setShowStoryForm(true); // Показываем форму
    setShowItemForm(false);
  };
  
  const handleCancelStoryForm = () => {
    setShowStoryForm(false);
    setEditingStory(null); // Сбрасываем редактируемую историю
    resetStoryForm();
  };
  
  // Обработчики для форм элементов истории
  const handleAddStoryItem = (storyId) => {
    setSelectedStoryId(storyId);
    setEditingItem(null);
    setFormMode('create');
    resetItemForm();
    
    setShowItemForm(true);
    setShowStoryForm(false);
  };
  
  const handleEditStoryItem = (storyId, item) => {
    setSelectedStoryId(storyId);
    setEditingItem(item);
    setFormMode('edit');
    
    // Заполняем форму данными элемента
    setType(item.type || 'image');
    
    setShowItemForm(true);
    setShowStoryForm(false);
  };
  
  const handleCancelItemForm = () => {
    setShowItemForm(false);
    resetItemForm();
  };
  
  // Обработка изменения файла
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      // Создаем URL для превью файла
      const fileUrl = URL.createObjectURL(selectedFile);
      setFilePreview(fileUrl);
    }
  };
  
  // Сохранение истории
  const handleSaveStory = async (e) => {
    e.preventDefault();
    
    try {
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      console.log('[StoryManagement] handleSaveStory: telegramId:', telegramId); // Добавлено логирование
      
      // Создаем градиент из выбранных цветов
      const backgroundColorValue = `linear-gradient(to bottom, ${topColor}, ${bottomColor})`;
      
      const storyData = {
        title,
        isActive,
        backgroundColor: backgroundColorValue,
        emoji
      };
      
      // Логируем данные перед отправкой
      console.log('[StoryManagement] Данные для сохранения (storyData):', JSON.stringify(storyData, null, 2));
      console.log('[StoryManagement] Form mode:', formMode);
      if(editingStory) {
        console.log('[StoryManagement] Editing story ID:', editingStory._id || editingStory.id);
      }

      let url = '/api/stories';
      let method = 'POST';
      
      if (formMode === 'edit' && editingStory) {
        url = `/api/stories/${editingStory._id || editingStory.id}`;
        method = 'PUT';
      }
      
      console.log('[StoryManagement] Request details:', { url, method });
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-Telegram-ID': telegramId
        },
        body: JSON.stringify(storyData)
      });
      
      if (!response.ok) {
        const errorBody = await response.text();
        console.error('[StoryManagement] Server error response:', errorBody);
        throw new Error(`Ошибка при сохранении истории. Статус: ${response.status}. Ответ: ${errorBody}`);
      }
      
      const result = await response.json();
      console.log('[StoryManagement] Ответ сервера при сохранении:', result);
      
      // Обновляем список историй
      fetchStories();
      
      // Закрываем форму
      setShowStoryForm(false);
      setEditingStory(null); 
      resetStoryForm();
      
      // Если это была новая история, предлагаем добавить элемент
      if (formMode === 'create' && result.story && result.story._id) {
        const addItem = window.confirm('История успешно создана! Добавить элемент в историю?');
        if (addItem) {
          handleAddStoryItem(result.story._id);
        }
      } else {
        alert(`История успешно ${formMode === 'create' ? 'создана' : 'обновлена'}`);
      }
    } catch (err) {
      console.error('Ошибка при сохранении истории (catch block):', err);
      alert(`Ошибка: ${err.message}`);
    }
  };
  
  // Сохранение элемента истории
  const handleSaveStoryItem = async (e) => {
    e.preventDefault();
    
    try {
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      
      // Проверяем наличие файла для нового элемента
      if (formMode === 'create' && !file) {
        alert('Необходимо загрузить файл для нового элемента истории');
        return;
      }
      
      // Создаем данные элемента истории
      const itemData = {
        type
      };
      
      // Создаем объект FormData для отправки файла
      const formData = new FormData();
      formData.append('data', JSON.stringify(itemData));
      
      // Если есть файл для загрузки, добавляем его
      if (file) {
        formData.append('file', file);
      }
      
      const headers = {
        'X-Telegram-ID': telegramId
      };
      
      let url, method;
      
      if (formMode === 'create') {
        url = `/api/stories/${selectedStoryId}/items`;
        method = 'POST';
      } else if (formMode === 'edit' && editingItem) {
        url = `/api/stories/${selectedStoryId}/items/${editingItem._id || editingItem.id}`;
        method = 'PUT';
      }
      
      const response = await fetch(url, {
        method,
        headers,
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Ошибка при сохранении элемента истории');
      }
      
      // Обновляем список историй
      fetchStories();
      
      // Закрываем форму
      setShowItemForm(false);
      resetItemForm();
      
      alert(`Элемент истории успешно ${formMode === 'create' ? 'добавлен' : 'обновлен'}`);
    } catch (err) {
      console.error('Ошибка при сохранении элемента истории:', err);
      alert(`Ошибка: ${err.message}`);
    }
  };
  
  // Удаление истории
  const handleDeleteStory = async (id) => {
    const confirmDelete = window.confirm('Вы уверены, что хотите удалить эту историю?');
    
    if (confirmDelete) {
      try {
        const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
        
        const response = await fetch(`/api/stories/${id}`, {
          method: 'DELETE',
          headers: {
            'X-Telegram-ID': telegramId
          }
        });
        
        if (!response.ok) {
          throw new Error('Ошибка при удалении истории');
        }
        
        // Обновляем список историй
        fetchStories();
        
        alert('История успешно удалена');
      } catch (err) {
        console.error('Ошибка при удалении истории:', err);
        alert(`Ошибка: ${err.message}`);
      }
    }
  };
  
  // Удаление элемента истории
  const handleDeleteStoryItem = async (storyId, itemId) => {
    const confirmDelete = window.confirm('Вы уверены, что хотите удалить этот элемент истории?');
    
    if (confirmDelete) {
      try {
        const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
        
        const response = await fetch(`/api/stories/${storyId}/items/${itemId}`, {
          method: 'DELETE',
          headers: {
            'X-Telegram-ID': telegramId
          }
        });
        
        if (!response.ok) {
          throw new Error('Ошибка при удалении элемента истории');
        }
        
        // Обновляем список историй
        fetchStories();
        
        alert('Элемент истории успешно удален');
      } catch (err) {
        console.error('Ошибка при удалении элемента истории:', err);
        alert(`Ошибка: ${err.message}`);
      }
    }
  };
  
  // Активация/деактивация истории
  const handleToggleActive = async (id, currentStatus) => {
    try {
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      
      const response = await fetch(`/api/stories/${id}/toggle-active`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Telegram-ID': telegramId
        }
      });
      
      if (!response.ok) {
        throw new Error('Ошибка при изменении статуса истории');
      }
      
      // Обновляем список историй
      fetchStories();
      
      alert(`История успешно ${currentStatus ? 'деактивирована' : 'активирована'}`);
    } catch (err) {
      console.error('Ошибка при изменении статуса истории:', err);
      alert(`Ошибка: ${err.message}`);
    }
  };
  
  // Предпросмотр истории
  const handlePreviewStory = (story) => {
    alert('Функция предпросмотра истории в разработке');
  };
  
  // Функция форматирования даты удалена, так как не используется
  
  return (
    <div className="story-management">
      <div className="story-management-header">
        <button className="add-story-button" onClick={handleAddStory}>
          <FaPlus /> Создать историю
        </button>
        <button className="sort-stories-button" onClick={toggleSortMode}>
          <FaSort /> {showSortMode ? 'Закрыть сортировку' : 'Сортировка'}
        </button>
      </div>
      
      {/* Режим сортировки без отдельной кнопки */}
      
      {/* Режим сортировки историй */}
      {showSortMode && (
        <div className="stories-sort-container">
          <h3>Упорядочите истории перетаскиванием</h3>
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Загрузка историй...</p>
            </div>
          ) : stories.length === 0 ? (
            <div className="empty-state">
              <p>Нет созданных историй</p>
            </div>
          ) : (
            <DndContext 
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={stories.map(story => story._id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="sortable-stories-list">
                  {stories.map(story => (
                    <SortableStoryItem 
                      key={story._id} 
                      story={story}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}
      
      {/* Форма создания/редактирования истории (для создания новой) */}
      {showStoryForm && !editingStory && formMode === 'create' && (
        <div className="story-form-container">
          <h3>Создание новой истории</h3>
          <form onSubmit={handleSaveStory} className="story-form">
            <div className="form-group">
              <label htmlFor="title">Название истории</label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Введите название истории"
              />
            </div>
            
            {/* Чекбокс "Активна" удален из формы создания */}
            
            <div className="form-group">
              <label>Цвета градиента фона</label>
              <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                <div>
                  <label htmlFor="topColor" style={{ fontSize: '0.85rem', display: 'block', marginBottom: '5px' }}>Верхний цвет</label>
                  <input
                    type="color"
                    id="topColor"
                    value={topColor}
                    onChange={(e) => setTopColor(e.target.value)}
                    style={{ width: '60px', height: '40px' }}
                  />
                </div>
                <div>
                  <label htmlFor="bottomColor" style={{ fontSize: '0.85rem', display: 'block', marginBottom: '5px' }}>Нижний цвет</label>
                  <input
                    type="color"
                    id="bottomColor"
                    value={bottomColor}
                    onChange={(e) => setBottomColor(e.target.value)}
                    style={{ width: '60px', height: '40px' }}
                  />
                </div>
              </div>
              <div 
                className="color-preview" 
                style={{ 
                  background: `linear-gradient(to bottom, ${topColor}, ${bottomColor})`,
                  width: '100%',
                  height: '40px',
                  borderRadius: '5px',
                  marginTop: '10px'
                }}
              ></div>
            </div>
            
            <div className="form-group">
              <label htmlFor="emoji">Эмодзи</label>
              <input
                type="text"
                id="emoji"
                value={emoji}
                onChange={(e) => setEmoji(e.target.value)}
                onKeyDown={(e) => {
                  // Предотвращаем отправку формы при нажатии Enter
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                placeholder="Введите эмодзи с клавиатуры, например: 😊"
                maxLength={2}
                style={{ fontSize: '24px', width: '60px', textAlign: 'center' }}
              />
            </div>
            
            <div className="form-actions">
              <button type="submit" className="save-button">
                Создать
              </button>
              <button type="button" className="cancel-button" onClick={handleCancelStoryForm}>
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Форма добавления/редактирования элемента истории ПЕРЕМЕЩЕНА ВНУТРЬ MAP */}
      
      {/* Отображение списка историй (не в режиме сортировки) */}
      {!showSortMode && loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Загрузка историй...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>Ошибка: {error}</p>
          <button onClick={fetchStories}>Повторить</button>
        </div>
      ) : stories.length === 0 ? (
        <div className="empty-state">
          <p>Нет созданных историй</p>
        </div>
      ) : (
        <div className="stories-list">
          {stories.map(story => {
            // Получаем ID и проверяем активность
            const storyId = story._id || story.id;
            const isActive = story.isActive !== undefined ? story.isActive : true;
            const hasItems = story.items && story.items.length > 0;
            
            return (
              <div key={storyId} className={`story-group-item ${isActive ? 'active' : 'inactive'}`}>
                {/* Форма редактирования истории (для конкретной истории) */}
                {editingStory && editingStory._id === storyId && formMode === 'edit' && (
                  <div className="story-form-container embedded-story-form"> {/* Добавляем класс для возможных специфичных стилей */}
                    <h3>Редактирование истории</h3>
                    <form onSubmit={handleSaveStory} className="story-form">
                      {/* Поля формы */}
                      <div className="form-group">
                        <label htmlFor="title">Название истории</label>
                        <input
                          type="text"
                          id="title"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          placeholder="Введите название истории"
                        />
                      </div>
                      {/* Чекбокс "Активна" удален из формы редактирования */}
                      <div className="form-group">
                        <label>Цвета градиента фона</label>
                        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                          <div>
                            <label htmlFor="topColor" style={{ fontSize: '0.85rem', display: 'block', marginBottom: '5px' }}>Верхний цвет</label>
                            <input type="color" id="topColor" value={topColor} onChange={(e) => setTopColor(e.target.value)} style={{ width: '60px', height: '40px' }} />
                          </div>
                          <div>
                            <label htmlFor="bottomColor" style={{ fontSize: '0.85rem', display: 'block', marginBottom: '5px' }}>Нижний цвет</label>
                            <input type="color" id="bottomColor" value={bottomColor} onChange={(e) => setBottomColor(e.target.value)} style={{ width: '60px', height: '40px' }} />
                          </div>
                        </div>
                        <div className="color-preview" style={{ background: `linear-gradient(to bottom, ${topColor}, ${bottomColor})`, width: '100%', height: '40px', borderRadius: '5px', marginTop: '10px' }}></div>
                      </div>
                      <div className="form-group">
                        <label htmlFor="emoji">Эмодзи</label>
                        <input
                          type="text"
                          id="emoji"
                          value={emoji}
                          onChange={(e) => setEmoji(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                          placeholder="Введите эмодзи с клавиатуры, например: 😊"
                          maxLength={2}
                          style={{ fontSize: '24px', width: '60px', textAlign: 'center' }}
                        />
                      </div>
                      <div className="form-actions">
                        <button type="submit" className="save-button">Сохранить</button>
                        <button type="button" className="cancel-button" onClick={handleCancelStoryForm}>Отмена</button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Отображение самой карточки истории, если она не редактируется */}
                {(!editingStory || editingStory._id !== storyId) && (
                  <>
                    <div className="story-group-layout">
                      <div className="story-preview-card" style={{ background: story.backgroundColor || 'linear-gradient(to bottom, #f09433, #e6683c)' }}>
                        <div className="story-preview-content">
                          <span className="story-emoji">{story.emoji || '😊'}</span>
                          <h3>{story.title || 'Без названия'}</h3>
                          <p className="story-items-count">Элементов: {hasItems ? story.items.length : 0}</p>
                        </div>
                      </div>
                      
                      <div className="story-controls">
                        <div className="story-top-actions">
                          <button 
                            className="content-action-button edit" 
                            onClick={() => handleEditStory(story)}
                          >
                            <FaEdit /> Редактировать
                          </button>
                          <button 
                            className="content-action-button add-item" 
                            onClick={() => handleAddStoryItem(storyId)}
                      >
                        <FaPlus /> Добавить сторис
                      </button>
                      <button 
                        className="content-action-button preview" 
                        onClick={() => handlePreviewStory(story)}
                      >
                        <FaEye /> Предпросмотр
                      </button>
                      <button
                        className="content-action-button delete" 
                        onClick={() => handleDeleteStory(storyId)}
                      >
                        <FaTrash /> Удалить
                      </button>
                      <button 
                        className={`content-action-button ${isActive ? 'unpublish' : 'publish'}`}
                        onClick={() => handleToggleActive(storyId, isActive)}
                      >
                        {isActive ? <FaToggleOff /> : <FaToggleOn />} 
                        {isActive ? "Деактивировать" : "Активировать"}
                      </button>
                    </div>
                  </div>
                </div> {/* Закрытие div className="story-group-layout" */}
                  </>
                )}
                
                {/* Отображение списка элементов истории (вне зависимости от режима редактирования самой истории) */}
                {/* Этот блок должен быть после блока карточки ИЛИ формы редактирования */}
                {hasItems && (!editingStory || editingStory._id !== storyId) ? ( /* Показываем список элементов если история не редактируется */
                  <div className="story-items-container">
                    <h4>Элементы истории:</h4>
                    <ul className="story-items-list"> {/* Заменено div на ul и класс */}
                      {story.items.map((item, index) => (
                        <li key={item._id || index} className="story-item-list-entry"> {/* Заменен div на li и класс */}
                          <div className="story-item-list-preview">
                            {item.type === 'video' || item.videoUrl ? (
                              <div className="video-icon-placeholder"> {/* Изменен класс для иконки видео */}
                                <FaVideo size={20} /> {/* Уменьшен размер иконки */}
                              </div>
                            ) : (
                              <div className="image-thumbnail-preview"> {/* Изменен класс для превью изображения */}
                                <img src={item.imageUrl} alt={`Элемент ${index + 1}`} />
                              </div>
                            )}
                          </div>
                          <div className="story-item-list-info">
                             <span className="story-item-type">{item.type === 'video' ? 'Видео' : 'Изображение'}</span>
                             {/* Можно добавить другую информацию, например, дату добавления */}
                          </div>
                          <div className="story-item-list-actions">
                            <button
                              className="content-action-button edit small"
                              onClick={() => handleEditStoryItem(storyId, item)}
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="content-action-button delete small"
                              onClick={() => handleDeleteStoryItem(storyId, item._id || item.id)}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  (!editingStory || editingStory._id !== storyId) && // Показываем кнопку "Добавить элемент" только если история не редактируется и нет элементов
                  <div className="empty-items">
                    <p>В этой карточке нет сторис</p>
                    <button 
                      className="add-item-button" 
                      onClick={() => handleAddStoryItem(storyId)}
                    >
                      <FaPlus /> Добавить сторис
                    </button>
                  </div>
                )}

                {/* Форма добавления/редактирования элемента истории (для конкретной истории) */}
                {showItemForm && selectedStoryId === storyId && (
                  <div className="story-form-container embedded-item-form"> {/* Класс для стилизации */}
                    <h3>
                      {formMode === 'create' 
                        ? 'Добавление элемента в историю' 
                        : 'Редактирование элемента истории'}
                    </h3>
                    <form onSubmit={handleSaveStoryItem} className="story-form">
                      <div className="form-group">
                        <label htmlFor="type">Тип</label>
                        <select
                          id="type"
                          value={type}
                          onChange={(e) => setType(e.target.value)}
                          required
                        >
                          <option value="image">Изображение</option>
                          <option value="video">Видео</option>
                        </select>
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="file">Загрузить файл</label>
                        <input
                          type="file"
                          id="file"
                          onChange={handleFileChange}
                          accept={type === 'image' ? "image/*" : "video/*"}
                          required={formMode === 'create'}
                        />
                      </div>
                      
                      {filePreview && (
                        <div className="form-group">
                          <label>Превью файла</label>
                          <div className="file-preview">
                            {type === 'image' ? (
                              <img src={filePreview} alt="Превью" />
                            ) : (
                              <video src={filePreview} controls width="100%" height="200"></video>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="form-actions">
                        <button type="submit" className="save-button">
                          {formMode === 'create' ? 'Добавить' : 'Сохранить'}
                        </button>
                        <button type="button" className="cancel-button" onClick={handleCancelItemForm}>
                          Отмена
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Компонент для сортируемого элемента истории
const SortableStoryItem = ({ story }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: story._id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
    position: 'relative'
  };

  return (
    <div ref={setNodeRef} style={style} className="sortable-story-item">
      <div className="story-item-content">
        <div 
          {...attributes} 
          {...listeners} 
          className="drag-handle" 
          style={{ 
            cursor: 'grab', 
            padding: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none'
          }}
        >
          <FaGripLines size={20} />
        </div>
        
        <div className="story-emoji-badge" 
          style={{ 
            background: story.backgroundColor || 'linear-gradient(to bottom, #f09433, #e6683c)',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            marginRight: '10px'
          }}
        >
          {story.emoji || '😊'}
        </div>
        
        <div className="story-name">
          <strong>{story.title || 'Без названия'}</strong>
          <div className="story-status">
            {story.isActive !== undefined && !story.isActive && (
              <span className="inactive-badge">Неактивна</span>
            )}
            {story.items && (
              <span className="items-count">Элементов: {story.items.length}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryManagement;
